// Espejo TS del resolver SQL de horario por (turno, día operativo) con
// vigencias (design horario-turno-dia §3; migraciones 085/086). Mirror de
// `turnos_horarios_dia` (mig 085) y del resolver `horario_turno_dia_core`.
//
// NO reemplaza al resolver SQL como fuente de verdad para el backend — el
// espejo TS existe para: (a) la vista previa en vivo del Coordinador en C11
// (§6, sin ida y vuelta al servidor por cada tecleo) y (b) el test de
// paridad SQL-vs-TS que prueba que ambas implementaciones coinciden
// (design §8 "Paridad").
import { z } from 'zod';
import { inicioFisicoTurno, diasSemanaOrdenados } from './turno.js';
const horaTimeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/;
export const turnoHorarioDiaSchema = z.object({
    id: z.string().uuid(),
    turno_id: z.string().uuid(),
    dia_semana: z.number().int().min(1).max(7),
    hora_inicio: z.string().regex(horaTimeRegex, 'Formato HH:MM o HH:MM:SS').nullable(),
    hora_fin: z.string().regex(horaTimeRegex, 'Formato HH:MM o HH:MM:SS').nullable(),
    cruza_medianoche: z.boolean(),
    no_opera: z.boolean(),
    vigente_desde: z.string().date(),
    vigente_hasta: z.string().date().nullable(),
});
const NOMBRES_DIA_ISO = {
    1: 'L',
    2: 'M',
    3: 'X',
    4: 'J',
    5: 'V',
    6: 'S',
    7: 'D',
};
function toMin(h) {
    const [hh, mm] = h.split(':');
    return Number(hh) * 60 + Number(mm);
}
function sumarMinutosIso(iso, minutos) {
    return new Date(Date.parse(iso) + minutos * 60_000).toISOString();
}
/**
 * Día ISO (1=lunes ... 7=domingo) de una fecha `YYYY-MM-DD`.
 */
export function diaSemanaIso(fechaISO) {
    const [y, m, d] = fechaISO.split('-').map(Number);
    const utc = new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1));
    const jsDay = utc.getUTCDay(); // 0=domingo ... 6=sábado
    return (jsDay === 0 ? 7 : jsDay);
}
function addDaysISO(fechaISO, plusDias) {
    const [y, m, d] = fechaISO.split('-').map(Number);
    if (!y || !m || !d)
        return fechaISO;
    return new Date(Date.UTC(y, m - 1, d + plusDias)).toISOString().slice(0, 10);
}
/**
 * Resuelve el horario físico de un turno para una fecha operativa, espejo de
 * `horario_turno_dia_core` (mig 085 §2.1). `horarios` puede traer TODAS las
 * vigencias del turno (histórico + vigente + programadas) — se filtra por
 * `dia_semana` y por la vigencia que cubre `fechaOperativa`.
 *
 * Retorna `null` si no hay ninguna fila vigente para ese día (sin
 * configuración) — una fila faltante equivale a `no_opera` en la BD (AD10),
 * pero como CONTRATO del espejo TS se distingue: `null` = "no hay dato para
 * validar/pintar todavía", el llamador decide cómo tratarlo.
 */
export function resolveHorarioTurno(horarios, fechaOperativa, atribucion) {
    const dia = diaSemanaIso(fechaOperativa);
    const candidato = horarios.find((h) => h.dia_semana === dia &&
        h.vigente_desde <= fechaOperativa &&
        (h.vigente_hasta === null || h.vigente_hasta >= fechaOperativa));
    if (!candidato)
        return null;
    if (candidato.no_opera) {
        return { fecha_operativa: fechaOperativa, no_opera: true };
    }
    const turnoBase = {
        hora_inicio: candidato.hora_inicio,
        hora_fin: candidato.hora_fin,
        cruza_medianoche: candidato.cruza_medianoche,
    };
    const inicioFisico = inicioFisicoTurno(fechaOperativa, turnoBase, atribucion);
    const duracionMin = toMin(turnoBase.hora_fin) -
        toMin(turnoBase.hora_inicio) +
        (turnoBase.cruza_medianoche ? 24 * 60 : 0);
    const finFisico = sumarMinutosIso(inicioFisico, duracionMin);
    return {
        fecha_operativa: fechaOperativa,
        no_opera: false,
        hora_inicio: turnoBase.hora_inicio,
        hora_fin: turnoBase.hora_fin,
        cruza_medianoche: turnoBase.cruza_medianoche,
        inicio_fisico: inicioFisico,
        fin_fisico: finFisico,
    };
}
/**
 * Entrada de `generarBloquesTurno` a partir de un `HorarioResuelto` operativo
 * (no `no_opera`). Espejo trivial — el resolver de bloques ya existe en
 * `turno.ts` y no se toca (design §3).
 */
export function horarioComoTurno(h) {
    return {
        hora_inicio: h.hora_inicio,
        hora_fin: h.hora_fin,
        cruza_medianoche: h.cruza_medianoche,
    };
}
/**
 * Instancias físicas de una lista de turnos (cada uno con su set de
 * `TurnoHorarioDia`) sobre una ventana `[desde, desde + dias - 1]`. Omite los
 * días sin configuración (`resolveHorarioTurno` devuelve `null`) y los días
 * `no_opera` — ninguno de los dos tiene rango físico que validar.
 */
export function instanciasSemana(turnos, desde, dias, atribucion) {
    const instancias = [];
    for (const turno of turnos) {
        for (let i = 0; i < dias; i++) {
            const fecha = addDaysISO(desde, i);
            const resuelto = resolveHorarioTurno(turno.horarios, fecha, atribucion);
            if (!resuelto || resuelto.no_opera)
                continue;
            instancias.push({
                turno_id: turno.turno_id,
                fecha_operativa: resuelto.fecha_operativa,
                inicio_fisico: resuelto.inicio_fisico,
                fin_fisico: resuelto.fin_fisico,
            });
        }
    }
    return instancias;
}
/**
 * Solapes (bloquean el guardado) y huecos (solo avisan, `0 < gap < 360 min`)
 * entre TODAS las instancias de la planta, sin importar el turno (design
 * §3, §6). Reutilizado por el servidor en el PUT de C11 (una sola
 * implementación compartida) y por la vista previa en vivo del FE.
 */
export function validarSemanaPlanta(inst) {
    const ordenadas = [...inst].sort((a, b) => Date.parse(a.inicio_fisico) - Date.parse(b.inicio_fisico));
    const solapes = [];
    for (let i = 0; i < ordenadas.length; i++) {
        const a = ordenadas[i];
        const aIni = Date.parse(a.inicio_fisico);
        const aFin = Date.parse(a.fin_fisico);
        for (let j = i + 1; j < ordenadas.length; j++) {
            const b = ordenadas[j];
            const bIni = Date.parse(b.inicio_fisico);
            if (bIni >= aFin)
                break; // ordenadas por inicio: ya no hay más solapes con `a`
            const bFin = Date.parse(b.fin_fisico);
            solapes.push({
                a: { turno_id: a.turno_id, fecha_operativa: a.fecha_operativa },
                b: { turno_id: b.turno_id, fecha_operativa: b.fecha_operativa },
                inicio: new Date(Math.max(aIni, bIni)).toISOString(),
                fin: new Date(Math.min(aFin, bFin)).toISOString(),
            });
        }
    }
    const huecos = [];
    let finMax = null;
    for (const cur of ordenadas) {
        const ini = Date.parse(cur.inicio_fisico);
        const fin = Date.parse(cur.fin_fisico);
        if (finMax && ini > finMax.fin) {
            const minutos = (ini - finMax.fin) / 60_000;
            if (minutos > 0 && minutos < 360) {
                huecos.push({
                    desde: {
                        turno_id: finMax.instancia.turno_id,
                        fecha_operativa: finMax.instancia.fecha_operativa,
                    },
                    hasta: { turno_id: cur.turno_id, fecha_operativa: cur.fecha_operativa },
                    minutos,
                });
            }
        }
        if (!finMax || fin > finMax.fin)
            finMax = { fin, instancia: cur };
    }
    return { solapes, huecos };
}
/**
 * Texto corto del horario semanal vigente de un turno, agrupando días
 * consecutivos (según `diaInicioSemana`) con el mismo horario:
 * "L-V 06:00-14:00 · S 06:00-11:00 · D no opera" (design §3, §6).
 * `horarios` debe traer una fila por día (la vigente de cada día).
 */
export function resumenHorarioSemanal(horarios, diaInicioSemana) {
    const orden = diasSemanaOrdenados(diaInicioSemana);
    const porDia = new Map();
    for (const h of horarios)
        porDia.set(h.dia_semana, h);
    const grupos = [];
    for (const dia of orden) {
        const h = porDia.get(dia);
        const etiquetaHorario = !h
            ? 'sin configuración'
            : h.no_opera
                ? 'no opera'
                : `${h.hora_inicio.slice(0, 5)}-${h.hora_fin.slice(0, 5)}`;
        const ultimo = grupos[grupos.length - 1];
        if (ultimo && ultimo.etiquetaHorario === etiquetaHorario) {
            ultimo.dias.push(dia);
        }
        else {
            grupos.push({ dias: [dia], etiquetaHorario });
        }
    }
    return grupos
        .map((g) => {
        const letra = g.dias.length === 1
            ? NOMBRES_DIA_ISO[g.dias[0]]
            : `${NOMBRES_DIA_ISO[g.dias[0]]}-${NOMBRES_DIA_ISO[g.dias[g.dias.length - 1]]}`;
        return `${letra} ${g.etiquetaHorario}`;
    })
        .join(' · ');
}
//# sourceMappingURL=turno_horario.js.map